/**
 * ModuleEditor — modal for creating or editing a training module.
 *
 * Sections:
 *   - Basic info: title, description, passing score, isDefault, isActive
 *   - Video: upload to S3 (presigned URL) + show current video preview
 *   - Quiz questions: add / edit / delete / reorder
 *
 * Pass moduleId='new' to create; pass a number to edit.
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  TrainingModule, TrainingQuestion, QuizOption, QuestionType,
  getModule, createModule, updateModule, deleteModule,
  createQuestion, updateQuestion, deleteQuestion,
  uploadVideo, uploadThumbnail, generateQuizFromContent,
} from '../../api/training';
import { modalBackdrop, modalCard, inputStyle, btnPrimary, btnSecondary } from './TrainingAdmin';

export function ModuleEditor({ moduleId, onClose, onSaved }: {
  moduleId: number | 'new';
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const isNew = moduleId === 'new';
  const [module, setModule] = useState<TrainingModule | null>(null);
  const [questions, setQuestions] = useState<TrainingQuestion[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Success toast — shown briefly after create/update so the user knows the save landed.
  const [successMsg, setSuccessMsg] = useState('');

  // Form state mirrors the module fields so we save in one shot.
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [passingScore, setPassingScore] = useState(70);
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isNew) {
      setTitle(''); setDescription(''); setPassingScore(70);
      setIsDefault(false); setIsActive(true);
      setModule(null); setQuestions([]);
      return;
    }
    setLoading(true);
    getModule(moduleId as number)
      .then(({ module: m, questions: q }) => {
        setModule(m); setQuestions(q);
        setTitle(m.title); setDescription(m.description || '');
        setPassingScore(m.passingScore);
        setIsDefault(m.isDefault); setIsActive(m.isActive);
      })
      .catch((err: any) => setError(err.message))
      .finally(() => setLoading(false));
  }, [moduleId, isNew]);

  async function saveCore() {
    if (!title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError('');
    try {
      // Guard against duplicate create: once `module` is set we switch to update
      // (covers the case where the user clicks "Create module" again after the AI
      // generation step has already persisted the module).
      if (isNew && !module) {
        const created = await createModule({
          title: title.trim(), description: description.trim() || undefined,
          passingScore, isDefault,
        });
        setModule(created);
        setSuccessMsg('Module created — add a video & questions below, then close.');
      } else if (module) {
        const updated = await updateModule(module.id, {
          title: title.trim(), description: description.trim() || null,
          passingScore, isDefault, isActive,
        });
        setModule(updated);
        setSuccessMsg('Changes saved.');
      }
      await onSaved();
      // Auto-dismiss the toast so the UI never feels frozen.
      setTimeout(() => setSuccessMsg(''), 3500);
    } catch (err: any) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function deleteSelf() {
    if (!module) return;
    if (!window.confirm(`Permanently delete module "${module.title}"? This also deletes all questions and assignments.`)) return;
    setSaving(true);
    try {
      await deleteModule(module.id);
      await onSaved();
      onClose();
    } catch (err: any) { setError(err.message); setSaving(false); }
  }

  return (
    <div onClick={() => !saving && onClose()} style={modalBackdrop}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 720 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, paddingBottom: 18, borderBottom: '1px solid var(--border)' }}>
          <div style={{
            width: 40, height: 40, borderRadius: 11, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--primary)', color: '#fff',
            boxShadow: '0 6px 16px -4px color-mix(in srgb, var(--primary) 55%, transparent)',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5" />
            </svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 3 }}>
              {isNew ? 'New training module' : 'Edit module'}
            </div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--fg)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isNew ? 'Create module' : (title || 'Edit module')}
            </div>
          </div>
          <button onClick={onClose} disabled={saving} className="tm-x" style={{
            marginLeft: 'auto', width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            background: 'transparent', border: '1px solid var(--border-md)', color: 'var(--muted)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {loading ? (
          <p style={{ color: 'var(--muted)' }}>Loading…</p>
        ) : (
          <>
            {/* ── Basic info ── */}
            <SectionHead>Basic information</SectionHead>
            <Field label="Title" required>
              <input
                value={title} onChange={e => setTitle(e.target.value)}
                className="tm-input"
                style={{ ...inputStyle, width: '100%' }}
                placeholder="e.g. Crisis Management Fundamentals"
              />
            </Field>
            <Field label="Description">
              <textarea
                value={description} onChange={e => setDescription(e.target.value)}
                rows={3}
                className="tm-input"
                style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
                placeholder="Brief overview of what this module covers."
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <Field label="Passing score (%)">
                <input
                  type="number" min={0} max={100}
                  value={passingScore}
                  onChange={e => setPassingScore(parseInt(e.target.value || '70', 10))}
                  className="tm-input"
                  style={{ ...inputStyle, width: '100%' }}
                />
              </Field>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 8, paddingBottom: 4 }}>
                <Toggle
                  label="Auto-assign to new staff"
                  checked={isDefault}
                  onChange={setIsDefault}
                />
                {!isNew && (
                  <Toggle
                    label="Active (visible to staff)"
                    checked={isActive}
                    onChange={setIsActive}
                  />
                )}
              </div>
            </div>

            {/* Hint shown only on create — the cover image, video and
                quiz panels appear after first save (they need a real
                module ID for S3 prefix + question rows). */}
            {isNew && !module && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 14px', marginBottom: 14,
                background: 'var(--primary-dim)',
                border: '1px solid var(--primary-border)',
                borderRadius: 10,
              }}>
                <span style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: 'var(--primary)', color: '#fff',
                  fontSize: 13, fontWeight: 700,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>i</span>
                <div style={{ fontSize: 12.5, color: 'var(--fg)', lineHeight: 1.5 }}>
                  <strong style={{ display: 'block', marginBottom: 2 }}>What's next after you save</strong>
                  <span style={{ color: 'var(--muted)' }}>
                    Hit <strong style={{ color: 'var(--fg)' }}>Create module</strong> below — then this dialog
                    expands with <strong style={{ color: 'var(--fg)' }}>Cover image</strong>,
                    <strong style={{ color: 'var(--fg)' }}> Video</strong> and
                    <strong style={{ color: 'var(--fg)' }}> Quiz questions</strong> panels you can fill in.
                  </span>
                </div>
              </div>
            )}

            {/* ── Thumbnail upload (only after the module exists) ── */}
            {module && <ThumbnailUploader module={module} onUpdated={async (m) => { setModule(m); await onSaved(); }} />}

            {/* ── Video upload (only after the module exists) ── */}
            {module && <VideoUploader module={module} onUpdated={async (m) => { setModule(m); await onSaved(); }} />}

            {/* ── Quiz questions (only after the module exists) ── */}
            {module && (
              <QuestionList
                moduleId={module.id}
                questions={questions}
                onChange={setQuestions}
                onSaved={onSaved}
              />
            )}

            {error && <div style={{ color: 'var(--accent-red)', fontSize: 12, margin: '12px 0' }}>⚠ {error}</div>}

            {/* Success popup — fixed-position overlay so it can't be
                missed (the inline-toast version Yash flagged as "not
                popping up"). Auto-dismisses via the existing 3.5s
                setTimeout that clears successMsg. Click anywhere on
                the dimmer to dismiss early. */}
            {successMsg && (
              <div
                onClick={() => setSuccessMsg('')}
                role="status"
                style={{
                  position: 'fixed', inset: 0, zIndex: 12000,
                  background: 'rgba(12,12,18,0.42)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  animation: 'moduleToastIn 220ms ease-out',
                }}
              >
                <div style={{
                  background: 'var(--card)', border: '1px solid var(--border-md)',
                  borderRadius: 14, padding: '24px 28px', maxWidth: 380,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
                  textAlign: 'center', cursor: 'default',
                }} onClick={(e) => e.stopPropagation()}>
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: 'rgba(34,197,94,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 14px',
                    animation: 'moduleSuccessPulse 480ms cubic-bezier(0.34,1.4,0.64,1)',
                  }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a"
                      strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <div style={{
                    fontSize: 17, fontWeight: 700, color: 'var(--fg)',
                    fontFamily: 'var(--font-heading)', marginBottom: 6,
                  }}>
                    Module created successfully
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>
                    {successMsg}
                  </div>
                  <button
                    onClick={() => setSuccessMsg('')}
                    style={{
                      marginTop: 16, background: 'var(--primary)', color: 'var(--on-primary)',
                      border: 'none', borderRadius: 8, padding: '8px 18px',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    Got it
                  </button>
                </div>
                <style>{`@keyframes moduleSuccessPulse { 0% { transform: scale(0.6); opacity: 0; } 60% { transform: scale(1.08); } 100% { transform: scale(1); opacity: 1; } }`}</style>
              </div>
            )}

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--border)',
            }}>
              {!isNew ? (
                <button onClick={deleteSelf} disabled={saving} style={{
                  ...btnSecondary,
                  color: 'var(--accent-red)', borderColor: 'rgba(237,28,36,0.30)',
                }}>Delete module</button>
              ) : <span />}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onClose} disabled={saving} style={btnSecondary}>
                  {module ? 'Done' : (isNew ? 'Cancel' : 'Close')}
                </button>
                <button onClick={saveCore} disabled={saving || !title.trim()} style={{
                  ...btnPrimary,
                  opacity: saving || !title.trim() ? 0.5 : 1,
                }}>
                  {saving ? 'Saving…' : (isNew && !module) ? 'Create module' : 'Save changes'}
                </button>
              </div>
            </div>
            <style>{`@keyframes moduleToastIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          </>
        )}
      </div>
    </div>
  );
}

// ── Field wrapper ───────────────────────────────────────────────────────

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{
        display: 'block', fontSize: 12, fontWeight: 600,
        color: 'var(--fg)', marginBottom: 6,
      }}>
        {label}{required && <span style={{ color: 'var(--accent-red)', marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

// Ledger-style section divider — uppercase label + hairline rule.
function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '4px 0 14px' }}>
      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg)', whiteSpace: 'nowrap' }}>{children}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--fg)' }}>
      {/* Real checkbox kept for keyboard + a11y; visually a switch. */}
      <input
        type="checkbox" checked={checked}
        onChange={e => onChange(e.target.checked)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
      />
      <span style={{
        width: 34, height: 20, borderRadius: 999, flexShrink: 0, position: 'relative',
        background: checked ? 'var(--primary)' : 'var(--border-md)', transition: 'background .15s',
      }}>
        <span style={{
          position: 'absolute', top: 2, left: checked ? 16 : 2, width: 16, height: 16,
          borderRadius: '50%', background: '#fff', transition: 'left .15s',
          boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
        }} />
      </span>
      {label}
    </label>
  );
}

// ── Video uploader ─────────────────────────────────────────────────────

// Thumbnail uploader — same two-step presigned-URL pattern as the video
// uploader but for an image shown on the module card on MyTraining. When
// no thumbnail is set, the card falls back to the default purple gradient
// + shield icon.
function ThumbnailUploader({ module, onUpdated }: {
  module: TrainingModule;
  onUpdated: (m: TrainingModule) => Promise<void>;
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    if (!file.type.startsWith('image/')) {
      setError('Please pick an image file (png, jpg, webp)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image too large (max 5 MB)');
      return;
    }
    setError(''); setUploading(true); setProgress(0);
    try {
      const publicUrl = await uploadThumbnail(module.id, file, (p) => setProgress(p));
      const updated = await updateModule(module.id, { thumbnailUrl: publicUrl });
      await onUpdated(updated);
    } catch (err: any) { setError(err.message); }
    finally { setUploading(false); setProgress(0); }
  }

  async function removeThumbnail() {
    setError('');
    try {
      const updated = await updateModule(module.id, { thumbnailUrl: null });
      await onUpdated(updated);
    } catch (err: any) { setError(err.message); }
  }

  return (
    <div style={{ marginBottom: 18, padding: 14, background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg)', marginBottom: 8 }}>
        Cover image
      </div>
      {module.thumbnailUrl ? (
        <div>
          <img
            src={module.thumbnailUrl}
            alt="Module cover"
            style={{
              width: '100%', maxHeight: 180, objectFit: 'cover',
              borderRadius: 6, background: 'var(--card)',
              aspectRatio: '16 / 9',
            }}
          />
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button onClick={() => fileInput.current?.click()} style={{ ...btnSecondary, padding: '5px 12px', fontSize: 11 }}>
              Replace image
            </button>
            <button onClick={removeThumbnail} style={{
              ...btnSecondary, padding: '5px 12px', fontSize: 11,
              color: 'var(--accent-red)', borderColor: 'rgba(237,28,36,0.3)',
            }}>
              Remove
            </button>
            <span style={{ fontSize: 11, color: 'var(--subtle)', alignSelf: 'center' }}>
              Shown on the staff "My Training" module card.
            </span>
          </div>
        </div>
      ) : (
        <button
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          style={{
            width: '100%', padding: '24px',
            background: 'transparent', border: '2px dashed var(--border-md)',
            borderRadius: 8, color: 'var(--muted)', fontSize: 13,
            cursor: uploading ? 'wait' : 'pointer', fontFamily: 'inherit',
          }}
        >
          {uploading
            ? `Uploading… ${progress}%`
            : 'Click to upload a cover image (png / jpg / webp · 16:9 works best · max 5 MB). Optional — falls back to the default gradient.'}
        </button>
      )}
      {uploading && (
        <div style={{ marginTop: 8, height: 4, background: 'var(--faint)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: 'var(--primary)', transition: 'width 0.2s' }} />
        </div>
      )}
      {error && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 8 }}>{error}</div>}
      <input
        ref={fileInput} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
      />
    </div>
  );
}

function VideoUploader({ module, onUpdated }: {
  module: TrainingModule;
  onUpdated: (m: TrainingModule) => Promise<void>;
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  async function handleFile(file: File) {
    // Browser-playable formats only. Yash hit "No video with supported
    // format and MIME type found" after uploading a .mov — browsers
    // don't reliably play QuickTime via the HTML5 <video> element.
    // Same applies to .avi/.mkv/.wmv/.flv. Restricting to mp4/webm at
    // upload time means we never store an unplayable video on S3.
    const SUPPORTED = ['video/mp4', 'video/webm'];
    if (!SUPPORTED.includes(file.type)) {
      const got = file.type || '(unknown type)';
      setError(`Only MP4 (H.264) and WebM are supported in browsers. You picked ${got}. To convert a .mov / .avi / .mkv to MP4, use HandBrake (free) or QuickTime → File → Export As → 1080p.`);
      return;
    }
    if (file.size > 500 * 1024 * 1024) {
      setError('Video too large (max 500 MB)');
      return;
    }
    // Pre-flight playability check: try to read duration from the file
    // BEFORE uploading. If the browser can't decode the metadata,
    // upload would just produce an unplayable file on S3. Hard fail
    // here so the user fixes the source file before wasting upload
    // bandwidth. (Previously this was `.catch(() => undefined)` — soft
    // fail — which is how the .mov files slipped through.)
    setError(''); setUploading(true); setProgress(0);
    let duration: number | undefined;
    try {
      duration = await readVideoDuration(file);
    } catch {
      setUploading(false);
      setError("Your browser can't decode this file's metadata — it likely uses an unsupported codec (e.g. HEVC / H.265 / ProRes). Re-encode to MP4 with H.264 video + AAC audio. HandBrake's 'Fast 1080p30' preset works well.");
      return;
    }
    try {
      const publicUrl = await uploadVideo(module.id, file, (p) => setProgress(p));
      const updated = await updateModule(module.id, {
        videoUrl: publicUrl,
        videoDurationSeconds: duration,
      });
      await onUpdated(updated);
    } catch (err: any) { setError(err.message); }
    finally { setUploading(false); setProgress(0); }
  }

  return (
    <div style={{ marginBottom: 18, padding: 14, background: 'var(--surface)', border: '1px solid var(--border-md)', borderRadius: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 700, color: 'var(--fg)', marginBottom: 8 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M10 9l5 3-5 3V9z" /></svg>
        Training video
      </div>
      {module.videoUrl ? (
        <div>
          <video
            ref={videoRef}
            src={module.videoUrl}
            controls
            onError={() => setError("This video can't be played by web browsers — it's likely a .mov, .avi, or HEVC-encoded .mp4. Click 'Replace video' below and upload an MP4 (H.264) or WebM file. HandBrake's 'Fast 1080p30' preset works for converting.")}
            style={{ width: '100%', maxHeight: 220, background: '#000', borderRadius: 6 }}
          />
          <div style={{ display: 'flex', gap: 10, marginTop: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>
              {module.videoDurationSeconds
                ? `${Math.floor(module.videoDurationSeconds / 60)}m ${module.videoDurationSeconds % 60}s`
                : 'Duration unknown'}
            </span>
            <button onClick={() => fileInput.current?.click()} style={{
              ...btnSecondary, padding: '5px 12px', fontSize: 11,
            }}>Replace video</button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => fileInput.current?.click()}
          disabled={uploading}
          style={{
            width: '100%', padding: '24px',
            background: 'transparent', border: '2px dashed var(--border-md)',
            borderRadius: 8, color: 'var(--muted)', fontSize: 13,
            cursor: uploading ? 'wait' : 'pointer', fontFamily: 'inherit',
          }}
        >
          {uploading
            ? `Uploading… ${progress}%`
            : 'Click to upload a training video — MP4 (H.264) or WebM only · max 500 MB. Convert .mov / .avi / HEVC sources first with HandBrake.'}
        </button>
      )}
      {uploading && (
        <div style={{ marginTop: 8, height: 4, background: 'var(--faint)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: 'var(--primary)', transition: 'width 0.2s' }} />
        </div>
      )}
      {error && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginTop: 8 }}>⚠ {error}</div>}
      <input
        ref={fileInput} type="file"
        // Restrict the OS file picker to known-playable formats. mp4/webm
        // are the universally browser-supported set. .mov is intentionally
        // excluded — QuickTime files (esp. iPhone exports) often use HEVC
        // codec that browsers can't decode.
        accept="video/mp4,video/webm"
        style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
      />
    </div>
  );
}

function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Math.round(video.duration));
    };
    video.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read video metadata')); };
    video.src = url;
  });
}

// ── Question list / editor ─────────────────────────────────────────────

function QuestionList({ moduleId, questions, onChange, onSaved }: {
  moduleId: number;
  questions: TrainingQuestion[];
  onChange: (q: TrainingQuestion[]) => void;
  onSaved: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [genContent, setGenContent] = useState('');
  const [genCount, setGenCount] = useState(6);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [genPreview, setGenPreview] = useState<TrainingQuestion[] | null>(null);

  async function handleGenerate() {
    if (genContent.trim().length < 50) { setGenError('Paste at least 50 characters of content.'); return; }
    setGenerating(true); setGenError('');
    try {
      const qs = await generateQuizFromContent(moduleId, genContent.trim(), genCount);
      setGenPreview(qs);
    } catch (err: any) { setGenError(err.message); }
    finally { setGenerating(false); }
  }

  async function saveGeneratedQuestions() {
    if (!genPreview) return;
    setGenerating(true);
    try {
      for (const q of genPreview) {
        // Pass questionType + matchOptions through! Without them the backend
        // defaults every question to single_choice, so a drag_match produced
        // by the AI ends up rendering as three radio buttons at quiz time
        // with no right-hand targets — the "match each operation" bug.
        await createQuestion(moduleId, {
          questionText:  q.questionText,
          questionType:  q.questionType,
          options:       q.options,
          matchOptions:  (q as any).matchOptions,
          correctAnswers: q.correctAnswers,
          explanation:   q.explanation || undefined,
          orderIndex:    questions.length + (q.orderIndex || 0),
        });
      }
      await onSaved();
      // Reload
      const { questions: updated } = await getModule(moduleId);
      onChange(updated);
      setGenPreview(null); setShowGenerate(false); setGenContent('');
    } catch (err: any) { setGenError(err.message); }
    finally { setGenerating(false); }
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg)', whiteSpace: 'nowrap' }}>
          Quiz questions{questions.length ? ` · ${questions.length}` : ''}
        </span>
        <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <button onClick={() => setShowGenerate(!showGenerate)} className="tm-pill" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: '1px solid color-mix(in srgb, var(--accent-gold) 32%, transparent)',
          borderRadius: 8, padding: '6px 12px', fontSize: 11.5, fontWeight: 700,
          color: 'var(--accent-gold)', cursor: 'pointer', fontFamily: 'inherit',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" /></svg>
          AI generate
        </button>
        <button onClick={() => setEditing('new')} className="tm-pill" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: '1px solid var(--primary-border)',
          borderRadius: 8, padding: '6px 12px', fontSize: 11.5, fontWeight: 700,
          color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit',
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Add manually
        </button>
      </div>

      {/* AI generation panel */}
      {showGenerate && (
        <div style={{
          padding: 14, marginBottom: 12,
          background: 'color-mix(in srgb, var(--accent-gold) 6%, transparent)',
          border: '1px solid color-mix(in srgb, var(--accent-gold) 24%, transparent)',
          borderRadius: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 700, color: 'var(--fg)', marginBottom: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent-gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" /></svg>
            Generate quiz from training content
          </div>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
            Paste the training material (video transcript, PDF text, notes) below <b>or upload a file</b> and the AI
            will generate a mix of question types: MCQ, multiple-select, ordering, matching, and short answer. You
            can review and edit before saving.
          </p>

          <GenContentFileUpload onText={(txt) => setGenContent(prev => prev ? `${prev}\n\n${txt}` : txt)} />

          <textarea
            value={genContent}
            onChange={e => setGenContent(e.target.value)}
            placeholder="Paste training content here (at least 50 characters)…"
            rows={6}
            style={{ ...inputStyle, width: '100%', resize: 'vertical', marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontSize: 12, color: 'var(--fg)', fontWeight: 600 }}>
              Questions:
              <input type="number" min={2} max={20} value={genCount}
                onChange={e => setGenCount(parseInt(e.target.value || '6', 10))}
                style={{ ...inputStyle, width: 60, marginLeft: 6 }} />
            </label>
            <button onClick={handleGenerate} disabled={generating} style={{
              ...btnPrimary, padding: '6px 16px', fontSize: 12,
              background: generating ? 'var(--faint)' : 'var(--accent-gold)',
              color: generating ? 'var(--muted)' : '#3a2600',
            }}>
              {generating ? 'Generating…' : 'Generate'}
            </button>
          </div>
          {genError && <div style={{ color: 'var(--accent-red)', fontSize: 12 }}>⚠ {genError}</div>}

          {/* Preview generated questions — user can drop bad ones or add
              their own alongside before the final save. Every row has a 🗑
              that removes just that question from the pending list. */}
          {genPreview && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg)', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Generated {genPreview.length} question{genPreview.length === 1 ? '' : 's'} — review, tweak, then save:</span>
                <button
                  onClick={() => setEditing('new')}
                  style={{
                    background: 'transparent', color: 'var(--primary)',
                    border: '1px solid var(--primary-border)', borderRadius: 6,
                    padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >+ Add another manually</button>
              </div>
              {genPreview.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', fontSize: 12, fontStyle: 'italic', background: 'var(--surface)', borderRadius: 6, marginBottom: 10 }}>
                  All generated questions were removed. Regenerate above or add your own manually.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10, maxHeight: 320, overflowY: 'auto' }}>
                  {genPreview.map((q, i) => (
                    <PreviewQuestionRow
                      key={i}
                      question={q}
                      onChange={(updated) => setGenPreview(prev => prev ? prev.map((x, idx) => idx === i ? updated : x) : prev)}
                      onDiscard={() => setGenPreview(prev => prev ? prev.filter((_, idx) => idx !== i) : prev)}
                    />
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setGenPreview(null)} style={{ ...btnSecondary, padding: '6px 14px', fontSize: 11 }}>Discard all</button>
                <button
                  onClick={saveGeneratedQuestions}
                  disabled={generating || genPreview.length === 0}
                  style={{ ...btnPrimary, padding: '6px 16px', fontSize: 11, opacity: (generating || genPreview.length === 0) ? 0.5 : 1 }}
                >
                  {generating ? 'Saving…' : `Save ${genPreview.length} question${genPreview.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {questions.length === 0 && !showGenerate ? (
        <p style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 0' }}>
          No questions yet. Add manually or use AI to generate a quiz from your training content.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {questions.map((q, i) => (
            <div key={q.id} style={{
              padding: '10px 12px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
            }}>
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: 'var(--primary-mid)', color: 'var(--primary)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>{i + 1}</span>
              <span style={{ flex: 1, color: 'var(--fg)' }}>{q.questionText}</span>
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                background: 'var(--faint)', color: 'var(--muted)', textTransform: 'uppercase',
              }}>{(q.questionType || 'single_choice').replace('_', ' ')}</span>
              <button onClick={() => setEditing(q.id)} style={{
                background: 'transparent', border: 'none',
                color: 'var(--primary)', cursor: 'pointer', fontSize: 11, fontWeight: 600,
              }}>Edit</button>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <QuestionEditor
          moduleId={moduleId}
          question={editing === 'new' ? null : (questions.find(q => q.id === editing) || null)}
          existingCount={questions.length}
          onClose={() => setEditing(null)}
          onSaved={async (saved, deleted) => {
            if (deleted) {
              onChange(questions.filter(q => q.id !== editing));
            } else if (saved) {
              if (editing === 'new') onChange([...questions, saved]);
              else onChange(questions.map(q => q.id === saved.id ? saved : q));
            }
            await onSaved();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

// PreviewQuestionRow — inline-editable row for an AI-generated question
// that hasn't been saved to the DB yet. Lets the IC tweak text / options /
// correct answer / explanation BEFORE bulk-saving the batch. Without this
// the IC could only delete-or-keep the AI's wording verbatim.
function PreviewQuestionRow({ question, onChange, onDiscard }: {
  question: TrainingQuestion;
  onChange: (q: TrainingQuestion) => void;
  onDiscard: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(question.questionText);
  const [opts, setOpts] = useState<QuizOption[]>(question.options || []);
  const [correct, setCorrect] = useState<string[]>(question.correctAnswers || []);
  const [explanation, setExplanation] = useState(question.explanation || '');

  // Re-sync local state when the parent rewires (regenerate, etc.)
  React.useEffect(() => {
    setText(question.questionText);
    setOpts(question.options || []);
    setCorrect(question.correctAnswers || []);
    setExplanation(question.explanation || '');
  }, [question]);

  function commit() {
    const filledOpts = opts.filter(o => o.text.trim());
    onChange({
      ...question,
      questionText: text.trim() || question.questionText,
      options: filledOpts.length ? filledOpts : question.options,
      correctAnswers: correct.length ? correct : question.correctAnswers,
      explanation: explanation.trim() || undefined,
    });
    setEditing(false);
  }

  if (!editing) {
    return (
      <div style={{
        padding: '8px 10px', background: 'var(--surface)',
        border: '1px solid var(--border)', borderRadius: 6,
        fontSize: 12,
        display: 'flex', alignItems: 'flex-start', gap: 8,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
              background: 'var(--primary-dim)', color: 'var(--primary)',
              textTransform: 'uppercase',
            }}>{question.questionType?.replace('_', ' ')}</span>
            <span style={{ color: 'var(--fg)', fontWeight: 600 }}>{question.questionText}</span>
          </div>
        </div>
        <button
          onClick={() => setEditing(true)}
          title="Edit this question before saving"
          style={{
            background: 'transparent', border: '1px solid var(--primary-border)',
            color: 'var(--primary)', cursor: 'pointer',
            fontSize: 10, fontWeight: 700, padding: '3px 9px',
            borderRadius: 4, fontFamily: 'inherit',
          }}
        >Edit</button>
        <button
          onClick={onDiscard}
          title="Discard this question"
          style={{
            background: 'transparent', border: 'none',
            color: 'var(--accent-red)', cursor: 'pointer',
            fontSize: 14, padding: '2px 6px', lineHeight: 1,
            opacity: 0.75, display: 'inline-flex', alignItems: 'center',
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '0.75'; }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
        </button>
      </div>
    );
  }

  // Edit mode — text + options + explanation, all in-memory. Saves the
  // updated question back to the parent's genPreview list on Save click.
  // Keeping it intentionally lightweight; the full QuestionEditor (post-
  // save edit) handles drag-match / multi-select / ordering complexity.
  return (
    <div style={{
      padding: '12px 14px', background: 'var(--card)',
      border: '1.5px solid var(--primary-border)', borderRadius: 8,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div>
        <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>
          Question
        </label>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={2}
          style={{
            width: '100%', resize: 'vertical', fontFamily: 'inherit',
            fontSize: 13, padding: '6px 8px',
            background: 'var(--surface)', color: 'var(--fg)',
            border: '1px solid var(--border-md)', borderRadius: 6,
          }}
        />
      </div>
      <div>
        <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>
          Options · radio = correct
        </label>
        {opts.map((o, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
            <input
              type="radio"
              name={`correct-preview-${question.id || question.questionText}`}
              checked={correct.includes(o.value)}
              onChange={() => setCorrect([o.value])}
            />
            <input
              type="text"
              value={o.text}
              onChange={e => setOpts(prev => prev.map((x, idx) => idx === i ? { ...x, text: e.target.value } : x))}
              style={{
                flex: 1, fontFamily: 'inherit', fontSize: 12,
                padding: '5px 8px',
                background: 'var(--surface)', color: 'var(--fg)',
                border: '1px solid var(--border-md)', borderRadius: 6,
              }}
            />
          </div>
        ))}
      </div>
      <div>
        <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>
          Explanation (shown to staff after they answer)
        </label>
        <textarea
          value={explanation}
          onChange={e => setExplanation(e.target.value)}
          rows={2}
          placeholder="Optional — why is this answer correct?"
          style={{
            width: '100%', resize: 'vertical', fontFamily: 'inherit',
            fontSize: 12, padding: '5px 8px',
            background: 'var(--surface)', color: 'var(--fg)',
            border: '1px solid var(--border-md)', borderRadius: 6,
          }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button
          onClick={() => setEditing(false)}
          style={{ ...btnSecondary, padding: '5px 14px', fontSize: 11 }}
        >Cancel</button>
        <button
          onClick={commit}
          style={{ ...btnPrimary, padding: '5px 16px', fontSize: 11 }}
        >Save edit</button>
      </div>
    </div>
  );
}

// Error boundary for the QuestionEditor modal — catches any render-time
// exception (e.g. an unexpected question shape from a future schema change)
// and shows a recoverable "something went wrong" panel instead of blanking
// the whole admin page. Lets the IC at least delete the bad question.
class QuestionEditorErrorBoundary extends React.Component<
  { onClose: () => void; onDelete?: () => void; children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: any) {
    console.error('[QuestionEditor] crashed:', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div onClick={this.props.onClose} style={{ ...modalBackdrop, zIndex: 10010 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 480 }}>
            <h4 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--fg)', marginBottom: 10 }}>
              Couldn't open this question
            </h4>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
              The question's data is in an unexpected shape (likely from an older AI generator). You can delete it and recreate, or close this dialog and try again.
            </p>
            <pre style={{ fontSize: 11, color: 'var(--accent-red)', background: 'var(--faint)', padding: 8, borderRadius: 6, overflow: 'auto', maxHeight: 120 }}>
              {String(this.state.error?.message || this.state.error)}
            </pre>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              {this.props.onDelete ? (
                <button onClick={this.props.onDelete} style={{ ...btnSecondary, color: 'var(--accent-red)', borderColor: 'rgba(237,28,36,0.30)', padding: '7px 14px', fontSize: 12 }}>
                  Delete question
                </button>
              ) : <span />}
              <button onClick={this.props.onClose} style={{ ...btnPrimary, padding: '7px 14px', fontSize: 12 }}>Close</button>
            </div>
          </div>
        </div>
      );
    }
    return <>{this.props.children}</>;
  }
}

function QuestionEditor({ moduleId, question, existingCount, onClose, onSaved }: {
  moduleId: number;
  question: TrainingQuestion | null;
  existingCount: number;
  onClose: () => void;
  onSaved: (saved: TrainingQuestion | null, deleted?: boolean) => Promise<void>;
}) {
  const handleDeleteFromBoundary = async () => {
    if (!question) return;
    if (!window.confirm('Delete this question?')) return;
    try { await deleteQuestion(moduleId, question.id); await onSaved(null, true); }
    catch (err: any) { alert(err.message); }
  };
  return (
    <QuestionEditorErrorBoundary
      onClose={onClose}
      onDelete={question ? handleDeleteFromBoundary : undefined}
    >
      <QuestionEditorBody
        moduleId={moduleId}
        question={question}
        existingCount={existingCount}
        onClose={onClose}
        onSaved={onSaved}
      />
    </QuestionEditorErrorBoundary>
  );
}

// Returns the canonical "empty correctAnswers" value for a question type.
// Centralised so the type switcher in the editor and the initial-state
// hooks both reset to a shape the editor knows how to render. drag_match
// is the odd one out — its correctAnswers is a {left:right} object map,
// not an array, which is exactly what crashed the old editor.
function emptyCorrect(type: QuestionType): any {
  return type === 'drag_match' ? {} : [];
}

// Coerce whatever shape the backend returned into something safe for the
// type. Defensive: if a single_choice question somehow has an object in
// correctAnswers (corrupted row), fall back to []. Saves us from the
// "edit drag_match → blanks the page" class of crash.
function coerceCorrect(type: QuestionType, raw: any): any {
  if (type === 'drag_match') {
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  }
  return Array.isArray(raw) ? raw : [];
}

function QuestionEditorBody({ moduleId, question, existingCount, onClose, onSaved }: {
  moduleId: number;
  question: TrainingQuestion | null;
  existingCount: number;
  onClose: () => void;
  onSaved: (saved: TrainingQuestion | null, deleted?: boolean) => Promise<void>;
}) {
  const isNew = !question;
  const [questionType, setQuestionType] = useState<QuestionType>(question?.questionType || 'single_choice');
  const [text, setText] = useState(question?.questionText || '');
  const [options, setOptions] = useState<QuizOption[]>(
    question?.options && question.options.length ? question.options : [
      { text: '', value: 'a' }, { text: '', value: 'b' },
      { text: '', value: 'c' }, { text: '', value: 'd' },
    ],
  );
  const [matchOptions, setMatchOptions] = useState<QuizOption[]>(
    question?.matchOptions && question.matchOptions.length ? question.matchOptions : [
      { text: '', value: 'r1' }, { text: '', value: 'r2' },
      { text: '', value: 'r3' }, { text: '', value: 'r4' },
    ],
  );
  const [correct, setCorrect] = useState<any>(coerceCorrect(questionType, question?.correctAnswers));
  const [explanation, setExplanation] = useState(question?.explanation || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function changeType(t: QuestionType) {
    setQuestionType(t);
    setCorrect(emptyCorrect(t));
  }

  function setOptionText(i: number, value: string) {
    setOptions(prev => prev.map((o, idx) => idx === i ? { ...o, text: value } : o));
  }
  function setMatchOptionText(i: number, value: string) {
    setMatchOptions(prev => prev.map((o, idx) => idx === i ? { ...o, text: value } : o));
  }
  function addOption() {
    const nextVal = String.fromCharCode('a'.charCodeAt(0) + options.length);
    setOptions(prev => [...prev, { text: '', value: nextVal }]);
  }
  function removeOption(value: string) {
    setOptions(prev => prev.filter(o => o.value !== value));
    // Clean up correctAnswers references to the removed option
    if (questionType === 'drag_match') {
      setCorrect((prev: Record<string, string>) => {
        const next = { ...prev };
        delete next[value];
        return next;
      });
    } else if (Array.isArray(correct)) {
      setCorrect((prev: string[]) => prev.filter(v => v !== value));
    }
  }
  function addMatchOption() {
    const nextVal = `r${matchOptions.length + 1}`;
    setMatchOptions(prev => [...prev, { text: '', value: nextVal }]);
  }
  function removeMatchOption(value: string) {
    setMatchOptions(prev => prev.filter(o => o.value !== value));
    // Clear any pair that mapped TO this target
    if (questionType === 'drag_match') {
      setCorrect((prev: Record<string, string>) => {
        const next: Record<string, string> = {};
        for (const [k, v] of Object.entries(prev || {})) {
          if (v !== value) next[k] = v;
        }
        return next;
      });
    }
  }

  async function save() {
    if (!text.trim()) { setError('Question text is required'); return; }
    const filledOptions = options.filter(o => o.text.trim());
    const filledTargets = matchOptions.filter(o => o.text.trim());

    // Per-type validation
    if (questionType === 'short_answer') {
      if (!Array.isArray(correct) || correct.length === 0) {
        setError('Add at least one acceptable keyword'); return;
      }
    } else if (questionType === 'drag_match') {
      if (filledOptions.length < 2)  { setError('Add at least 2 left-side items');  return; }
      if (filledTargets.length < 2)  { setError('Add at least 2 right-side targets'); return; }
      const pairs = correct as Record<string, string>;
      const matched = filledOptions.filter(o => pairs[o.value]);
      if (matched.length !== filledOptions.length) {
        setError('Pick a target for every left-side item'); return;
      }
    } else if (questionType === 'drag_order') {
      if (filledOptions.length < 2)  { setError('Add at least 2 items to order'); return; }
      if (!Array.isArray(correct) || correct.length !== filledOptions.length) {
        setError('Set the correct order for every item'); return;
      }
    } else {
      // single_choice / multiple_choice
      if (filledOptions.length < 2) { setError('Need at least 2 answer options'); return; }
      const corr = Array.isArray(correct) ? correct : [];
      if (questionType === 'single_choice') {
        if (corr.length !== 1) { setError('Mark exactly one option as correct'); return; }
      } else {
        if (corr.length < 1) { setError('Mark at least one option as correct'); return; }
      }
      if (!corr.every(v => filledOptions.find(o => o.value === v))) {
        setError('Correct answer references a missing option'); return;
      }
    }

    setSaving(true); setError('');
    try {
      const payload: any = {
        questionType,
        questionText: text.trim(),
        options: filledOptions,
        correctAnswers: correct,
        explanation: explanation.trim() || undefined,
      };
      if (questionType === 'drag_match') payload.matchOptions = filledTargets;
      const saved = isNew
        ? await createQuestion(moduleId, { ...payload, orderIndex: existingCount + 1 })
        : await updateQuestion(moduleId, question!.id, payload);
      await onSaved(saved);
    } catch (err: any) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function del() {
    if (!question) return;
    if (!window.confirm('Delete this question?')) return;
    setSaving(true);
    try {
      await deleteQuestion(moduleId, question.id);
      await onSaved(null, true);
    } catch (err: any) { setError(err.message); setSaving(false); }
  }

  return (
    <div onClick={() => !saving && onClose()} style={{ ...modalBackdrop, zIndex: 10010 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 640 }}>
        <h4 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--fg)', marginBottom: 14 }}>
          {isNew ? 'Add quiz question' : 'Edit question'}
        </h4>

        <Field label="Question type" required>
          <select
            value={questionType}
            onChange={e => changeType(e.target.value as QuestionType)}
            style={{ ...inputStyle, width: '100%' }}
          >
            <option value="single_choice">Single choice — pick one</option>
            <option value="multiple_choice">Multiple choice — select all that apply</option>
            <option value="drag_order">Drag &amp; order — put items in sequence</option>
            <option value="drag_match">Drag &amp; match — pair items left to right</option>
            <option value="short_answer">Short answer — keyword match</option>
          </select>
        </Field>

        <Field label="Question" required>
          <textarea
            value={text} onChange={e => setText(e.target.value)} rows={2}
            placeholder="e.g. What is the first action after a CRITICAL incident is declared?"
            style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
          />
        </Field>

        {/* ── Per-type editors ─────────────────────────────────────────── */}
        {(questionType === 'single_choice' || questionType === 'multiple_choice') && (
          <Field label={questionType === 'single_choice'
            ? 'Answer options (mark exactly one as correct)'
            : 'Answer options (mark all correct)'} required>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {options.map((opt, i) => {
                const corr: string[] = Array.isArray(correct) ? correct : [];
                const isCorrect = corr.includes(opt.value);
                return (
                  <div key={opt.value} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '4px 8px',
                    background: isCorrect ? 'rgba(74,222,128,0.10)' : 'transparent',
                    borderRadius: 6,
                  }}>
                    <input
                      type={questionType === 'single_choice' ? 'radio' : 'checkbox'}
                      checked={isCorrect}
                      onChange={() => {
                        if (questionType === 'single_choice') setCorrect([opt.value]);
                        else setCorrect((prev: string[]) => isCorrect ? prev.filter(v => v !== opt.value) : [...prev, opt.value]);
                      }}
                      style={{ accentColor: '#4ade80' }}
                    />
                    <span style={{ fontSize: 12, color: 'var(--muted)', minWidth: 14 }}>{opt.value.toUpperCase()}.</span>
                    <input
                      value={opt.text}
                      onChange={e => setOptionText(i, e.target.value)}
                      placeholder={`Option ${opt.value.toUpperCase()}`}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    {options.length > 2 && (
                      <button onClick={() => removeOption(opt.value)} style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} title="Remove option">×</button>
                    )}
                  </div>
                );
              })}
              <button onClick={addOption} style={{ ...btnSecondary, padding: '5px 10px', fontSize: 11, alignSelf: 'flex-start' }}>+ Add option</button>
            </div>
          </Field>
        )}

        {questionType === 'drag_order' && (
          <Field label="Items in correct order (use ▲▼ to reorder)" required>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {options.map((opt, i) => (
                <div key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: 'var(--faint)', borderRadius: 6 }}>
                  <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700, minWidth: 20 }}>{i + 1}.</span>
                  <input
                    value={opt.text}
                    onChange={e => setOptionText(i, e.target.value)}
                    placeholder={`Step ${i + 1}`}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button disabled={i === 0} onClick={() => {
                    setOptions(prev => { const n = [...prev]; [n[i-1], n[i]] = [n[i], n[i-1]]; return n; });
                  }} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', cursor: i === 0 ? 'not-allowed' : 'pointer', fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>▲</button>
                  <button disabled={i === options.length - 1} onClick={() => {
                    setOptions(prev => { const n = [...prev]; [n[i+1], n[i]] = [n[i], n[i+1]]; return n; });
                  }} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', cursor: i === options.length - 1 ? 'not-allowed' : 'pointer', fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>▼</button>
                  {options.length > 2 && (
                    <button onClick={() => removeOption(opt.value)} style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} title="Remove">×</button>
                  )}
                </div>
              ))}
              <button onClick={addOption} style={{ ...btnSecondary, padding: '5px 10px', fontSize: 11, alignSelf: 'flex-start' }}>+ Add step</button>
              <p style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 0' }}>
                When the trainee plays this question we'll shuffle the items; they have to drag them into the order shown above.
              </p>
            </div>
            {/* Capture the current visible order as the correct answer
                whenever the order/text changes — keeps `correct` in sync
                without needing an explicit "set correct order" button. */}
            <DragOrderSync options={options} setCorrect={setCorrect} />
          </Field>
        )}

        {questionType === 'drag_match' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Left-side items">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {options.map((opt, i) => (
                  <div key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 18 }}>{opt.value}</span>
                    <input
                      value={opt.text}
                      onChange={e => setOptionText(i, e.target.value)}
                      placeholder="Item"
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    {options.length > 2 && (
                      <button onClick={() => removeOption(opt.value)} style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} title="Remove">×</button>
                    )}
                  </div>
                ))}
                <button onClick={addOption} style={{ ...btnSecondary, padding: '5px 10px', fontSize: 11, alignSelf: 'flex-start' }}>+ Add item</button>
              </div>
            </Field>
            <Field label="Right-side targets">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {matchOptions.map((opt, i) => (
                  <div key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11, color: 'var(--muted)', minWidth: 22 }}>{opt.value}</span>
                    <input
                      value={opt.text}
                      onChange={e => setMatchOptionText(i, e.target.value)}
                      placeholder="Target"
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    {matchOptions.length > 2 && (
                      <button onClick={() => removeMatchOption(opt.value)} style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }} title="Remove">×</button>
                    )}
                  </div>
                ))}
                <button onClick={addMatchOption} style={{ ...btnSecondary, padding: '5px 10px', fontSize: 11, alignSelf: 'flex-start' }}>+ Add target</button>
              </div>
            </Field>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Correct pairing — pick the matching target for each left item" required>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {options.filter(o => o.text.trim()).map(opt => {
                    const pairs = (correct && typeof correct === 'object' && !Array.isArray(correct) ? correct : {}) as Record<string, string>;
                    return (
                      <div key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: pairs[opt.value] ? 'rgba(74,222,128,0.10)' : 'var(--faint)', borderRadius: 6 }}>
                        <span style={{ fontSize: 12, color: 'var(--fg)', flex: 1 }}>{opt.text || `(item ${opt.value})`}</span>
                        <span style={{ fontSize: 13, color: 'var(--muted)' }}>↔</span>
                        <select
                          value={pairs[opt.value] || ''}
                          onChange={e => setCorrect((prev: Record<string, string>) => ({ ...prev, [opt.value]: e.target.value }))}
                          style={{ ...inputStyle, flex: 1 }}
                        >
                          <option value="">— Pick target —</option>
                          {matchOptions.filter(t => t.text.trim()).map(t => (
                            <option key={t.value} value={t.value}>{t.text}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </Field>
            </div>
          </div>
        )}

        {questionType === 'short_answer' && (
          <Field label="Acceptable keywords (any one of these in the answer = correct)" required>
            <ShortAnswerKeywords correct={Array.isArray(correct) ? correct : []} setCorrect={setCorrect} />
            <p style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 0' }}>
              The trainee passes if any of these keywords (case-insensitive substring) appears in their typed answer.
            </p>
          </Field>
        )}

        <Field label="Explanation (shown when graded)">
          <textarea
            value={explanation} onChange={e => setExplanation(e.target.value)} rows={2}
            placeholder="Why this answer is correct — gives the trainee context after they submit."
            style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
          />
        </Field>

        {error && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginBottom: 10 }}>⚠ {error}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {!isNew ? (
            <button onClick={del} disabled={saving} style={{
              ...btnSecondary,
              color: 'var(--accent-red)', borderColor: 'rgba(237,28,36,0.30)',
              padding: '7px 14px', fontSize: 12,
            }}>Delete</button>
          ) : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={saving} style={{ ...btnSecondary, padding: '7px 14px', fontSize: 12 }}>
              Cancel
            </button>
            <button onClick={save} disabled={saving} style={{ ...btnPrimary, padding: '7px 16px', fontSize: 12 }}>
              {saving ? 'Saving…' : 'Save question'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Tiny side-effect helper: keeps `correct` (the answer key for drag_order)
// in lockstep with the visible order of options. Without this, IC has to
// remember to manually re-enter the order array — easy to forget and a
// common source of "saved but it's wrong" bugs in the old QuestionEditor.
function DragOrderSync({ options, setCorrect }: { options: QuizOption[]; setCorrect: (v: any) => void }) {
  React.useEffect(() => {
    const filledValues = options.filter(o => o.text.trim()).map(o => o.value);
    setCorrect(filledValues);
  }, [options, setCorrect]);
  return null;
}

// Chip-style keyword editor used by short_answer questions. Type a word,
// hit Enter, it becomes a removable chip.
function ShortAnswerKeywords({ correct, setCorrect }: { correct: string[]; setCorrect: (v: any) => void }) {
  const [draft, setDraft] = useState('');
  function add() {
    const k = draft.trim();
    if (!k) return;
    if (correct.includes(k)) { setDraft(''); return; }
    setCorrect([...correct, k]);
    setDraft('');
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {correct.map(k => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px 3px 10px', background: 'var(--primary-mid)', color: 'var(--primary)', fontSize: 11, fontWeight: 600, borderRadius: 12 }}>
            {k}
            <button onClick={() => setCorrect(correct.filter(c => c !== k))} style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Type a keyword and press Enter"
          style={{ ...inputStyle, flex: 1 }}
        />
        <button onClick={add} disabled={!draft.trim()} style={{ ...btnSecondary, padding: '5px 12px', fontSize: 12 }}>Add</button>
      </div>
    </div>
  );
}

// ── File upload for AI quiz generator ────────────────────────────────────
// Admin can drop a .txt / .md / .csv / .json file (training notes, a
// transcript, a briefing doc) and its text gets appended into the genContent
// textarea. PDFs and DOCX would need a parsing lib bundled in — kept out of
// the main bundle for now; user sees a clear message if they pick one.
function GenContentFileUpload({ onText }: { onText: (txt: string) => void }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>('');
  const [err, setErr] = useState<string>('');
  const [busy, setBusy] = useState(false);

  const TEXT_EXT = ['.txt', '.md', '.csv', '.json', '.log', '.srt', '.vtt'];

  async function handleFile(file: File) {
    setErr(''); setFileName(file.name); setBusy(true);
    try {
      const lower = file.name.toLowerCase();
      const ok = TEXT_EXT.some(ext => lower.endsWith(ext)) || file.type.startsWith('text/');
      if (!ok) {
        if (lower.endsWith('.pdf') || lower.endsWith('.docx') || lower.endsWith('.doc')) {
          setErr(`For ${lower.split('.').pop()!.toUpperCase()} files, open them in any reader → Select All → Copy → paste into the textarea below. Text-based files (.txt, .md, .csv, .json, .srt, .vtt) upload directly.`);
        } else {
          setErr(`Unsupported file type. Use a plain-text file (.txt, .md, .csv, .json, .srt, .vtt) or paste the content below.`);
        }
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErr('File too large (max 5 MB of text).');
        return;
      }
      const text = await file.text();
      const cleaned = text.trim();
      if (cleaned.length < 20) {
        setErr('File seems empty.');
        return;
      }
      onText(cleaned);
    } catch (e: any) {
      setErr(e.message || 'Could not read file');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginBottom: 10 }}>
      <input
        ref={fileInput}
        type="file"
        accept=".txt,.md,.csv,.json,.log,.srt,.vtt,text/*"
        style={{ display: 'none' }}
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => fileInput.current?.click()}
        disabled={busy}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'color-mix(in srgb, var(--accent-gold) 8%, transparent)',
          border: '1.5px dashed color-mix(in srgb, var(--accent-gold) 40%, transparent)',
          borderRadius: 8, padding: '10px 14px', width: '100%',
          color: 'var(--accent-gold)', fontSize: 12, fontWeight: 600, cursor: busy ? 'wait' : 'pointer',
          fontFamily: 'inherit',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" /></svg>
        {busy
          ? 'Reading…'
          : fileName
            ? <span>Added <b>{fileName}</b> — appended below. Pick another or type more.</span>
            : <span>Upload training file (.txt, .md, .csv, .json, .srt, .vtt) — text gets appended to the box below</span>}
      </button>
      {err && (
        <div style={{ color: 'var(--accent-red)', fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
          ⚠ {err}
        </div>
      )}
    </div>
  );
}
